import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock'
import { gsap, Power2 } from 'gsap'
import { useState, useEffect, useContext, useRef } from 'react'
import { useTranslation } from 'next-i18next'
import Router from 'next/router'
import useSWR, { useSWRConfig } from 'swr'

// Internal components
import CreateTrigger from '../CreateTrigger/CreateTrigger'
import FormPrompt from '../FormPrompt/FormPrompt'
import Legend from '../Legend/Legend'
import Loader from '../Loader/Loader'
import ModalBack from '../ModalBack/ModalBack'
import ModalCopylink from '../ModalCopylink/ModalCopylink'
import ModalForm from '../ModalForm/ModalForm'
import ModalFrame from '../ModalFrame/ModalFrame'
import ModalOnboarding from '../ModalOnboarding/ModalOnboarding'
import ModalTitle from '../ModalTitle/ModalTitle'
import RichTextBlock from '../RichTextBlock/RichTextBlock'
import ShareOptions from '../ShareOptions/ShareOptions'

// Context
import { ModalContext } from '../../context/ModalContext'
import { UserContext } from '../../context/UserContext'

const modalApiUrl = process.env.NEXT_PUBLIC_MODAL_API
const loadedClass = 'is-loaded'

const ModalContent = () => {
  // Context
  const modalContext = useContext(ModalContext)
  const userContext = useContext(UserContext)
  // Translations
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )

  // Refs
  const contentRef = useRef()
  const modalSectionRef = useRef()

  // State
  const [urlState, setUrlState] = useState(false)
  const [fetchState, setFetchState] = useState(false)
  const [dataFetched, setDataFetched] = useState(false)
  const [timeline] = useState(() => gsap.timeline({ reversed: true, paused: true }))

  // SWR
  const { cache } = useSWRConfig()
  const fetcher = (...args) => fetch(...args).then((res) => res.json())
  const { data, error } = useSWR(fetchState === true ? urlState : null, fetcher)

  // On SWR data recived set data-fetch-state
  useEffect(() => {
    if (data && dataFetched !== data) {
      if (data?.[0]?.sections) {
        setDataFetched(data)
      } else if (data?.[0]?.redirect) {
        Router.push(data?.[0]?.redirect)
      } else if (data?.[0]?.overwrite) {
        overwriteModalIntent(data)
      }
      cache.clear()
    }
  }, [data])

  // On SWR error recieved log error
  useEffect(() => {
    if (error) console.error('error:', error)
  }, [error])

  useEffect(() => {
    if (modalContext.modalScrollTop) scrollToTop()
  }, [modalContext.modalScrollTop])

  // OnMount prepare gsap timeline
  useEffect(() => {
    timeline.to(contentRef.current, { height: 'auto', duration: 0.5, delay: 1, ease: Power2.easeInOut })
    if (modalContext.modalIsActive) disableBodyScroll(contentRef.current)
    return () => clearAllBodyScrollLocks()
  }, [])

  // On route change fetch new data
  useEffect(() => {
    if (!modalContext.modalRoute) return
    const url = `${modalApiUrl}/${modalContext.modalRoute}${modalContext.modalId ? `/${modalContext.modalId}` : ''}`
    setFetchState(true)
    setUrlState(url)
  }, [modalContext.modalRoute])

  // On response change change data fetched
  useEffect(() => {
    if (modalContext.modalResponse?.[0]?.sections && dataFetched !== modalContext.modalResponse) applyModalResponse()
  }, [modalContext.modalResponse])

  // on data-fetch-state change update animation & modifiers
  useEffect(() => {
    if (!dataFetched) return false
    addLoadedClass()
    timeline.reversed() ? timeline.play() : timeline.play()
  }, [dataFetched])

  const applyModalResponse = () => {
    setDataFetched(modalContext.modalResponse)
    modalContext.setModalRoute(false)
    modalContext.setModalId(false)
    setFetchState(false)
    scrollToTop()
  }

  const overwriteModalIntent = (data) => {
    setDataFetched(false)
    modalContext.setModalId(false)
    modalContext.setModalRoute(data?.[0]?.overwrite)
    modalContext.setModalMandatory(data?.[0]?.mandatory)
    if (data?.[0]?.modifier) modalContext.setModalModifier(data?.[0]?.modifier)
    if (data?.[0]?.return) modalContext.setModalReturn(data?.[0]?.return)
  }

  const addLoadedClass = () => {
    const timer = setTimeout(() => modalContext.setStateClass(loadedClass), 10)
    return () => clearTimeout(timer)
  }

  const scrollToTop = () => {
    contentRef.current.scrollTo({ top: 0 })
    modalContext.setModalScrollTop(false)
  }
  return (
    <>
      <div className="ModalContent" ref={contentRef}>
        <div className="ModalContent-inner">
          {dataFetched?.[0]?.sections &&
            dataFetched[0].sections.map((section, index) => {
              return (
                <div ref={modalSectionRef} className="ModalContent-section" key={`ModalContent-section--${index}`}>
                  {section.elements.map((element, index) => {
                    switch (element.type) {
                      case 'titlebar':
                        return <ModalTitle {...element} key={`${element.typeHandle}-${index}`} />
                      case 'prompt':
                        return <FormPrompt {...element} key={`${element.typeHandle}-${index}`} />
                      case 'header':
                      case 'text': {
                        const copy = element?.copy
                        const copyContent = !copy
                          ? null
                          : !copy.transl
                          ? copy.text
                          : !copy.vars
                          ? t(copy.transl)
                          : t(copy.transl, copy.vars)

                        const copyData = {}
                        if (element.type === 'header') copyData.h3 = copyContent
                        if (element.type === 'text') copyData.p = copyContent

                        return (
                          <RichTextBlock
                            data={[copyData]}
                            modifiers={['RichTextBlock--modalContent', `${element.modifier}`]}
                            key={`${element.type}-${index}`}
                          />
                        )
                      }
                      case 'back':
                        return <ModalBack {...element} key={`${element.type}-${index}`} />
                      case 'form':
                        return <ModalForm {...element} key={`${element.type}-${index}`} />
                      case 'frame':
                        return <ModalFrame {...element} key={`${element.type}-${index}`} />
                      case 'legend':
                        return <Legend {...element} key={`${element.type}-${index}`} />
                      case 'onboarding':
                        return <ModalOnboarding {...element} key={`${element.type}-${index}`} />
                      case 'createTrigger':
                        return <CreateTrigger {...element} key={`${element.type}-${index}`} />
                      case 'shareOptions':
                        return <ShareOptions {...element} key={`${element.type}-${index}`} />
                      case 'copylink':
                        return <ModalCopylink {...element} key={`${element.type}-${index}`} />
                    }
                    return ''
                  })}
                </div>
              )
            })}
        </div>
      </div>
      <div className="ModalContent-loader">
        <Loader modifiers={['Modal-loadIcon']} />
      </div>
    </>
  )
}

export default ModalContent
